import { useState, useEffect } from 'react'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { Password, PasswordCreateData, PasswordUpdateData, PasswordGroup } from '../types'

interface PasswordFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  password?: Password | null
  decryptedPassword?: string
  groups: PasswordGroup[]
  defaultGroupId?: number | null
  onSubmit: (data: PasswordCreateData | PasswordUpdateData) => Promise<void>
  loading?: boolean
}

export function PasswordForm({
  open,
  onOpenChange,
  password,
  decryptedPassword,
  groups,
  defaultGroupId,
  onSubmit,
  loading,
}: PasswordFormProps) {
  const isEditing = !!password
  const [showPassword, setShowPassword] = useState(false)
  const [formData, setFormData] = useState<PasswordCreateData>({
    nombre: '',
    username: '',
    password: '',
    url: '',
    notas: '',
    group_id: null,
  })

  useEffect(() => {
    if (password) {
      setFormData({
        nombre: password.nombre,
        username: password.username,
        password: decryptedPassword || '',
        url: password.url,
        notas: password.notas,
        group_id: password.group_id,
      })
    } else {
      setFormData({
        nombre: '',
        username: '',
        password: '',
        url: '',
        notas: '',
        group_id: defaultGroupId ?? null,
      })
    }
    setShowPassword(false)
  }, [password, decryptedPassword, open, defaultGroupId])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (isEditing) {
      // Solo enviar campos que cambiaron
      const updateData: PasswordUpdateData = {}
      if (formData.nombre !== password.nombre) updateData.nombre = formData.nombre
      if (formData.username !== password.username) updateData.username = formData.username
      if (formData.password && formData.password !== decryptedPassword) updateData.password = formData.password
      if (formData.url !== password.url) updateData.url = formData.url
      if (formData.notas !== password.notas) updateData.notas = formData.notas
      if (formData.group_id !== password.group_id) {
        updateData.group_id = formData.group_id === null ? 0 : formData.group_id
      }

      await onSubmit(updateData)
    } else {
      await onSubmit(formData)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Editar Contraseña' : 'Nueva Contraseña'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="nombre">Nombre del servicio *</Label>
            <Input
              id="nombre"
              placeholder="Ej: API OpenAI"
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="group">Grupo</Label>
            <Select
              value={formData.group_id?.toString() || "none"}
              onValueChange={(value) => setFormData({
                ...formData,
                group_id: value === "none" ? null : parseInt(value)
              })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sin grupo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin grupo</SelectItem>
                {groups.map((group) => (
                  <SelectItem key={group.id} value={group.id.toString()}>
                    {group.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="username">Usuario / Email</Label>
            <Input
              id="username"
              placeholder="usuario@ejemplo.com"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">
              Contraseña {!isEditing && '*'}
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder={isEditing ? '(sin cambios)' : 'Contraseña'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!isEditing}
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full px-3"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="url">URL del servicio</Label>
            <Input
              id="url"
              type="url"
              placeholder="https://ejemplo.com"
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notas">Notas</Label>
            <Textarea
              id="notas"
              placeholder="Notas adicionales..."
              value={formData.notas}
              onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-teal-800 hover:bg-teal-900 text-white"
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              {isEditing ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
